import { useState, useEffect } from "react";
import { TrashIcon, MessageSquareIcon, XIcon, CheckCheckIcon, RotateCcw } from "lucide-react";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { Button } from "../../../Components/Buttons";
import { ToastMessage, getSelectedIds } from "../../../Components/Utils";
import { handleError } from "../../../stores/components/handleError";
import {
  useFetchContactMessagesQuery,
  useFetchTrashedContactMessagesQuery,
  useReplyContactMessageMutation,
  useDeleteContactMessageMutation,
  useDestroyContactMessageMutation,
  useRestoreContactMessageMutation,
  useBulkDeleteContactMessagesMutation,
  useBulkDestroyContactMessagesMutation,
} from "../../../stores/api/contacts.api";
import contactsHelper from "../../../stores/api/helpers/contacts.helper";
import payloadHelper from "../../../stores/api/helpers/payload.helper";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const HEADERS = [
  { key: "id", label: "#" },
  { key: "full_name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "subject", label: "Subject" },
];

const MessageModal = ({ row, onClose, onReply, sending }) => {
  const [reply, setReply] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setReply("");
    setSent(Boolean(row?.replied_at || row?.reply_message));
  }, [row?.id, row?.replied_at, row?.reply_message]);

  if (!row) return null;

  const handleSend = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    try {
      await onReply(row.id, reply.trim());
      setSent(true);
      setReply("");
    } catch {
      // handled upstream
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-gray-100 dark:border-slate-700 p-6 flex flex-col gap-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-slate-800 dark:hover:text-gray-200 transition">
          <XIcon size={16} />
        </button>

        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-orange-100 dark:bg-orange-500/10 flex items-center justify-center flex-shrink-0">
            <MessageSquareIcon size={16} className="text-orange-500" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">{row.name}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{row.email}</p>
          </div>
        </div>

        <div className="rounded-lg bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 p-4 space-y-2">
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">{row.subject}</p>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{row.message}</p>
        </div>

        {row.reply_message && (
          <div className="rounded-lg bg-orange-50 dark:bg-orange-500/10 border border-orange-100 dark:border-orange-500/20 p-3 space-y-1">
            <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">Previous Reply</p>
            <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">{row.reply_message}</p>
          </div>
        )}

        {sent ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-green-600 dark:text-green-400 font-medium">Reply sent to {row.email}</p>
            <button type="button" onClick={onClose} className="px-4 py-1.5 text-xs font-semibold rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600 transition">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSend} className="flex flex-col gap-3">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Reply to {row.email}</label>
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Type your reply..." className="w-full border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none transition" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-4 py-1.5 text-xs font-semibold rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600 transition">Cancel</button>
              <button type="submit" disabled={sending || !reply.trim()} className="px-4 py-1.5 text-xs font-semibold rounded-md bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition">
                {sending ? "Sending…" : "Send Reply"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const ContactMessagesList = () => {
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selected, setSelected] = useState(null);

  const queryParams = payloadHelper.forList({
    page,
    limit,
    search,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchContactMessagesQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedContactMessagesQuery(queryParams, { skip: !trashed });
  const { data: messages = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [replyMessage, { isLoading: sending }] = useReplyContactMessageMutation();
  const [deleteMessage] = useDeleteContactMessageMutation();
  const [destroyMessage] = useDestroyContactMessageMutation();
  const [restoreMessage] = useRestoreContactMessageMutation();
  const [bulkDelete] = useBulkDeleteContactMessagesMutation();
  const [bulkDestroy] = useBulkDestroyContactMessagesMutation();

  const resetPage = () => setPage(1);

  const handleReply = async (id, replyText) => {
    try {
      await replyMessage(contactsHelper.forReply(id, { reply_message: replyText })).unwrap();
      ToastMessage({ type: "success", title: "Sent!", message: "Reply sent." });
    } catch (err) {
      handleError(err, "Failed to send reply.");
      throw err;
    }
  };

  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: `${verb} Message`,
      message: `${verb} message from "${row.full_name}"?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") await restoreMessage(row.id).unwrap();
          else if (type === "destroy") await destroyMessage(row.id).unwrap();
          else await deleteMessage(row.id).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `Message ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed.");
        }
      },
    });
  };

  const handleBulk = (selectedItems, type = "delete") => {
    const ids = getSelectedIds(selectedItems);
    const verb = type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: "Bulk Action",
      message: `${verb} ${ids.length} messages?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(ids);
          if (type === "destroy") await bulkDestroy(body).unwrap();
          else await bulkDelete(body).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `${ids.length} messages ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  return (
    <>
      <BreadCrumb title="Contact Messages" />
      <TableListing
        headers={HEADERS}
        paginationData={messages}
        loading={isFetching}
        handleBulk={handleBulk}
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
        actions={(row) => (
          <div className="flex gap-2">
            {!trashed ? (
              <Button
                onClick={() => setSelected(row)}
                title={row.reply_message ? "Replied" : "View Message"}
                icon={row.reply_message ? CheckCheckIcon : MessageSquareIcon}
                className={
                  row.reply_message
                    ? "text-green-500 hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-500/10"
                    : "text-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-500/10"
                }
              />
            ) : (
              <Button
                onClick={() => handleSingle(row, "restore")}
                title="Restore"
                icon={RotateCcw}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
            )}
            <Button
              onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
              title="Delete"
              icon={TrashIcon}
              className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
            />
          </div>
        )}
      />
      <MessageModal row={selected} onClose={() => setSelected(null)} onReply={handleReply} sending={sending} />
    </>
  );
};

export default ContactMessagesList;
